/**
 * Aircraft state and the flight model. Kinematic, not aerodynamic: the aircraft turns at
 * a rate, climbs at a rate and accelerates at a rate from performance.ts, and moves along
 * its track. Guidance (what heading/altitude/speed to aim for) comes from the nav state
 * set by pilot.ts; this file turns it into motion and handles the automatic phases
 * (take-off roll, rotation, localizer capture, glideslope, touchdown, rollout, hold).
 */
import { alongTrackM, bearingDeg, crossTrackM, destination, distanceM, FT_TO_M, KT_TO_MPS, NM_TO_M, toMagneticDeg, toTrueDeg, wrap180, type AircraftCategory, type AirportData, type AssignedClearance, type FlightKind, type LatLon, type RealismOptions, type RouteLeg, type RunwayEnd, type TurnDirection } from '@atc/core';
import { climbRateFpm, EXPEDITE_FACTOR, tasFromIas, turnRateDegS, type Performance } from './performance';
import type { Emergency } from './emergency';
import type { FuelState } from './fuel';
import type { PilotMemory } from './pilot-brain';
import type { ControlRecord } from './operations';
import type { CallsignForm } from './airlines';
import type { CrewStyle } from './phraseology';
import { inRunwayArea, nearestGroundNode, runwayExit, taxiPath, updateSurfaceClearance } from './surface';

export type Phase =
  | 'taxiOut' | 'holdShort' | 'lineUp' | 'linedUp' | 'takeoffRoll' | 'abortRoll'
  | 'departure' | 'enroute' | 'approach' | 'final' | 'goAround'
  | 'rollout' | 'vacating' | 'landed' | 'handedOff' | 'exited'
  /** A departure that waited too long for clearance and went back to the stand. */
  | 'cancelled' | 'crashed'
  /** The tanks ran dry: the flight is over, wherever it is. */
  | 'forcedLanding';

/** `published`: inbound course, turn and leg from the chart, kept whichever way the aircraft arrives; otherwise the inbound course is the one it arrived on, with right turns. */
export interface HoldState { fix: string; inboundDeg: number; legS: number; stage: 'toFix' | 'outbound' | 'inbound'; t: number; turn?: TurnDirection; published?: boolean; maxIasKt?: number }

export interface NavState {
  mode: 'heading' | 'direct' | 'route' | 'hold' | 'localizer';
  headingDeg: number;
  turn?: TurnDirection;
  fix?: string;
  route?: RouteLeg[];
  routeIdx: number;
  hold?: HoldState;
}

export interface ApproachState {
  runway: RunwayEnd;
  established: boolean;
  onSlope: boolean;
  /** Set when the aircraft flew past the threshold without ever capturing the localizer. */
  missed: boolean;
  /** Cleared to land. An ILS approach clearance alone ends in a go-around at 1 nm. */
  cleared: boolean;
  /** The pilot has already called short final asking for landing clearance. */
  requestedLanding?: boolean;
  /** Distance to the threshold when the clearance was given, nm: a diverging vector only grows it. */
  clearedAtNm?: number;
  /** Side of the final (sign of the cross-track) when the clearance was given; crossing to the other side uncaptured is an overshoot. */
  clearedSide?: number;
  /**
   * Cleared on the way to a fix on the published approach ("cleared ILS via GODED"): the fixes still
   * to fly before the localizer, each with the lowest altitude the procedure allows there.
   */
  via?: { fix: string; altitudeFt?: number }[];
  /** The procedure fix last passed: from there the crew tracks the leg's course to the next one, as an FMS does, instead of homing. */
  viaFrom?: string;
}

export interface Aircraft {
  control?: ControlRecord;
  crossing?: { runway: string; approved: boolean; entered: boolean };
  exitRoute?: { lat: number; lon: number }[];
  /** A rejected take-off has stopped and is vacating the runway. */
  abortStopped?: boolean;
  holdPosition?: boolean;
  takeoffAtAgeS?: number;
  pilot: PilotMemory;
  id: number;
  callsign: string;
  airline: string;
  /** How this crew says its flight number (sim/src/airlines.ts); absent means digit by digit. */
  callsignForm?: CallsignForm;
  /** How this crew talks, fixed for the flight; absent is the standard crew (see `phraseology.ts`). */
  crewStyle?: CrewStyle;
  type: string;
  category: AircraftCategory;
  kind: FlightKind;
  perf: Performance;
  lat: number;
  lon: number;
  altitudeFt: number;
  headingDeg: number;
  trackDeg: number;
  iasKt: number;
  groundspeedKt: number;
  verticalSpeedFpm: number;
  onGround: boolean;
  phase: Phase;
  /** Seconds in the current phase and since spawn. */
  phaseT: number;
  ageS: number;
  nav: NavState;
  targetAltitudeFt: number;
  expedite: boolean;
  /** Controller-assigned speed; null means "pilot's discretion". */
  targetIasKt: number | null;
  assigned: AssignedClearance;
  requested: string;
  /** Filed route (STAR, or SID up to the requested exit): direct-to a fix on it and "resume own navigation" rejoin it. */
  flightPlan: RouteLeg[];
  /** Name of the STAR or SID the flight plan came from. */
  procedure?: string;
  /** Fuel in minutes of flying; arrivals only (sim/src/fuel.ts). */
  fuel?: FuelState;
  /** People on board, when the controller asks. */
  persons?: number;
  /** Declared emergency, while it lasts (sim/src/emergency.ts). */
  emergency?: Emergency;
  /** A departure coming back to the field: it is not handed off at its exit fix. */
  returning?: boolean;
  approach?: ApproachState;
  /** Runway assigned for this arrival before an approach clearance is issued. */
  arrivalRunway?: RunwayEnd;
  /** Runway used for departure (the end the aircraft is holding at). */
  departureRunway?: RunwayEnd;
  /** Pending: after line-up, roll automatically (T given without W). */
  rollAfterLineUp: boolean;
  conflict: boolean;
  /** The short-term conflict alert predicts a separation loss within a minute. */
  conflictWarning: boolean;
  /** Sim time at which to delete the aircraft (after landing/handoff/exit). */
  removeAtS?: number;
  /** Where the aircraft holds short (for abort / respawn positioning). */
  holdShortPos?: { lat: number; lon: number; headingDeg: number };
  /** OSM taxiway path to the assigned departure runway. */
  taxiRoute?: { lat: number; lon: number }[];
  taxiRouteIdx?: number;
  /** Last altitude the pilot complained about (high realism radio chatter). */
  lastRequestS: number;
}

export interface Wind { directionDeg: number; speedKt: number }

export interface StepContext {
  airport: AirportData;
  opts: RealismOptions;
  wind: Wind;
  simTimeS: number;
  /** Runway pair names ("10L/28R") currently occupied by a rolling/landing aircraft. */
  occupiedRunways: Set<string>;
  /** Live traffic, used for low-speed ground following. */
  traffic?: readonly Aircraft[];
  emit: (ev: AircraftEvent) => void;
}

export type GoAroundReason = 'controller' | 'unstable' | 'occupied' | 'noClearance';

export type AircraftEvent =
  | { type: 'airborne'; ac: Aircraft }
  | { type: 'established'; ac: Aircraft }
  | { type: 'touchdown'; ac: Aircraft; runway: string }
  | { type: 'clearOfRunway'; ac: Aircraft; runway: string }
  | { type: 'goAround'; ac: Aircraft; reason: string; reasonCode: GoAroundReason }
  /** Short final on an ILS approach without landing clearance. */
  | { type: 'requestLanding'; ac: Aircraft }
  | { type: 'missedIntercept'; ac: Aircraft }
  | { type: 'handoff'; ac: Aircraft; fix: string; manual?: boolean }
  | { type: 'exited'; ac: Aircraft }
  /** `next`: continuing the flight plan, the plan ended here, or the fix was not on the plan (the pilot asks for more). */
  /** `hold`: the fix was the clearance limit and has a published hold, which the crew enters. */
  | { type: 'reachedFix'; ac: Aircraft; fix: string; next: 'route' | 'endOfRoute' | 'offRoute' | 'hold' }
  | { type: 'abortedTakeoff'; ac: Aircraft };

const GROUND_PHASES: ReadonlySet<Phase> = new Set(['taxiOut', 'holdShort', 'lineUp', 'linedUp', 'takeoffRoll', 'abortRoll', 'rollout', 'vacating', 'landed']);
export const isOnGroundPhase = (p: Phase) => GROUND_PHASES.has(p);
/** Holding short, lining up or lined up: a departure that has not started its roll. */
export const isAwaitingDeparture = (p: Phase) => p === 'holdShort' || p === 'lineUp' || p === 'linedUp';
export const isRemovedPhase = (p: Phase) => p === 'handedOff' || p === 'exited' || p === 'landed' || p === 'cancelled' || p === 'forcedLanding' || p === 'crashed';

/**
 * Altitude to level at on a go-around: 3 000 ft above the field, rounded up to a thousand, but
 * never below where the aircraft already is. A go-around flown from final is always a climb; one
 * the controller calls for an aircraft still at 8 000 ft must not turn into a descent.
 */
export function goAroundAltitudeFt(airport: AirportData, ac?: Aircraft): number {
  const missed = Math.ceil((airport.elevationFt + 3000) / 1000) * 1000;
  return ac ? Math.max(missed, Math.round(ac.altitudeFt / 100) * 100) : missed;
}

/**
 * Above this much of the glideslope inside 3 nm the approach cannot be salvaged: the descent on the
 * slope is capped at 1 500 fpm and there is about a minute left, so anything higher never gets down.
 * Mid and high realism go around on the tighter stabilisation criteria long before this.
 */
const UNLANDABLE_FT = 2000;
/** tan(1°): the half-width of the localizer beam in which a nearly parallel aircraft captures it. */
const BEAM_CAPTURE_TAN = Math.tan(Math.PI / 180);
/** Extra distance from the threshold, nm, before an aircraft pointing away from the runway gives up on the approach. */
const DIVERGED_NM = 5;

/**
 * Passed a fix being flown to: within the turn-anticipation distance, or abeam and close. With the
 * next leg's course known the turn is flown by, leading it by R·tan(turn/2) so the aircraft rolls
 * out on the next leg instead of swinging wide of it. The turn is measured from the inbound leg's
 * course when there is one (not the heading, which may still be turning onto it).
 */
function passingFix(ac: Aircraft, fix: LatLon, tasKt: number, opts: RealismOptions, nextCourseDeg?: number, inboundCourseDeg = ac.headingDeg): boolean {
  const d = distanceM(ac.lat, ac.lon, fix.lat, fix.lon);
  const turnRadiusM = (tasKt * KT_TO_MPS) / ((turnRateDegS(tasKt, ac.perf.bankDeg, opts.bankLimitedTurns) * Math.PI) / 180);
  const turn = nextCourseDeg === undefined ? 0 : Math.min(Math.abs(wrap180(nextCourseDeg - inboundCourseDeg)), 150);
  const lead = nextCourseDeg === undefined ? turnRadiusM * 0.6 : Math.min(2.5 * turnRadiusM, turnRadiusM * Math.tan((turn / 2) * Math.PI / 180));
  const abeam = Math.abs(wrap180(bearingDeg(ac.lat, ac.lon, fix.lat, fix.lon) - ac.headingDeg)) > 90 && d < 2 * NM_TO_M;
  return d < Math.max(0.3 * NM_TO_M, lead) || abeam;
}

/** The published hold at a fix as a hold state, or undefined when the airport publishes none there. */
export function publishedHoldState(airport: AirportData, fix: string, stage: HoldState['stage']): HoldState | undefined {
  const h = airport.xAtc.holds?.find((x) => x.fix === fix);
  return h ? { fix, inboundDeg: toTrueDeg(airport, h.inboundDeg), legS: h.legS, stage, t: 0, turn: h.turn, published: true, ...(h.maxIasKt ? { maxIasKt: h.maxIasKt } : {}) } : undefined;
}

export function setPhase(ac: Aircraft, phase: Phase): void {
  if (ac.phase !== phase) { ac.phase = phase; ac.phaseT = 0; }
}

export function runwayPairName(airport: AirportData, end: RunwayEnd): string {
  return airport.runways.find((r) => r.ends.some((e) => e.name === end.name))?.name ?? end.name;
}

/**
 * Start a go-around: runway heading (present heading if there was no approach), climb to the
 * go-around altitude, approach and speed cancelled. Split from `goAround` so `pilot.ts` can fly
 * an instructed one without a `StepContext`; both paths produce the same state and the same event.
 */
export function applyGoAround(ac: Aircraft, airport: AirportData, reason: string, reasonCode: GoAroundReason): AircraftEvent {
  const rw = ac.approach?.runway;
  ac.nav = { mode: 'heading', headingDeg: rw?.headingDeg ?? ac.headingDeg, routeIdx: 0 };
  ac.targetAltitudeFt = goAroundAltitudeFt(airport, ac);
  ac.expedite = false;
  ac.targetIasKt = null;
  ac.approach = undefined;
  ac.assigned = { ...ac.assigned, runway: undefined, speedKt: undefined, expedite: false, altitudeFt: ac.targetAltitudeFt, headingDeg: Math.round(toMagneticDeg(airport, ac.nav.headingDeg)) || 360, fix: undefined, hold: undefined };
  ac.onGround = false;
  setPhase(ac, 'goAround');
  return { type: 'goAround', ac, reason, reasonCode };
}

export function goAround(ac: Aircraft, ctx: StepContext, reason: string): void {
  ctx.emit(applyGoAround(ac, ctx.airport, reason, reason === 'unstable approach' ? 'unstable' : reason === 'no landing clearance' ? 'noClearance' : 'occupied'));
}

/** Taxiways from here back to the holding point, or straight there when there is no taxi graph. */
function taxiBack(airport: AirportData, from: LatLon, hold: LatLon): LatLon[] {
  const net = airport.groundNetwork;
  const path = net?.nodes.length ? taxiPath(net, nearestGroundNode(net, from), nearestGroundNode(net, hold)) : undefined;
  return [...(path ?? []), hold];
}

export function stepAircraft(ac: Aircraft, dt: number, ctx: StepContext): void {
  if (ac.phase === 'crashed') return;
  const { airport, opts } = ctx;
  ac.ageS += dt; ac.phaseT += dt;
  const fieldFt = airport.elevationFt;

  // --- Ground phases ---
  switch (ac.phase) {
    case 'taxiOut': {
      updateSurfaceClearance(ac, airport);
      if (ac.crossing && !ac.crossing.approved) { ac.iasKt = ac.groundspeedKt = 0; return; }
      const next = ac.taxiRoute?.[ac.taxiRouteIdx ?? 0];
      if (!next) { setPhase(ac, 'holdShort'); ac.iasKt = ac.groundspeedKt = 0; return; }
      const d = distanceM(ac.lat, ac.lon, next.lat, next.lon);
      if (d < 5) {
        ac.taxiRouteIdx = (ac.taxiRouteIdx ?? 0) + 1;
        if (ac.taxiRouteIdx >= (ac.taxiRoute?.length ?? 0)) {
          if (ac.holdShortPos) { ac.lat = ac.holdShortPos.lat; ac.lon = ac.holdShortPos.lon; ac.headingDeg = ac.holdShortPos.headingDeg; ac.trackDeg = ac.headingDeg; }
          setPhase(ac, 'holdShort'); ac.iasKt = ac.groundspeedKt = 0; return;
        }
      }
      const target = ac.taxiRoute?.[ac.taxiRouteIdx ?? 0]!;
      const desired = bearingDeg(ac.lat, ac.lon, target.lat, target.lon);
      turnToward(ac, desired, 18, dt);
      const blocked = ctx.traffic?.some((o) => o !== ac && o.onGround && !isRemovedPhase(o.phase) && distanceM(ac.lat, ac.lon, o.lat, o.lon) < 50 && Math.abs(wrap180(bearingDeg(ac.lat, ac.lon, o.lat, o.lon) - ac.headingDeg)) < 45);
      if (blocked) { ac.iasKt = ac.groundspeedKt = 0; ac.verticalSpeedFpm = 0; return; }
      ac.iasKt = 12; ac.groundspeedKt = 12; ac.trackDeg = ac.headingDeg; ac.verticalSpeedFpm = 0;
      moveAlong(ac, ac.headingDeg, 12, dt);
      return;
    }
    case 'holdShort':
      ac.iasKt = 0; ac.groundspeedKt = 0; ac.verticalSpeedFpm = 0;
      return;
    case 'lineUp': {
      if (ac.holdPosition) { ac.iasKt = ac.groundspeedKt = 0; return; }
      const rw = ac.departureRunway!;
      const d = distanceM(ac.lat, ac.lon, rw.lat, rw.lon);
      const desired = d > 12 ? bearingDeg(ac.lat, ac.lon, rw.lat, rw.lon) : rw.headingDeg;
      turnToward(ac, desired, 20, dt);
      ac.iasKt = d > 12 ? 12 : 0;
      moveAlong(ac, ac.headingDeg, ac.iasKt, dt);
      if (d <= 12 && Math.abs(wrap180(rw.headingDeg - ac.headingDeg)) < 1.5) {
        ac.headingDeg = rw.headingDeg; ac.lat = rw.lat; ac.lon = rw.lon;
        setPhase(ac, ac.rollAfterLineUp ? 'takeoffRoll' : 'linedUp');
        ac.rollAfterLineUp = false;
      }
      return;
    }
    case 'linedUp':
      ac.iasKt = 0; ac.groundspeedKt = 0; ac.verticalSpeedFpm = 0;
      return;
    case 'takeoffRoll': {
      ac.iasKt += ac.perf.rollAccelKtS * dt;
      moveAlong(ac, ac.headingDeg, ac.iasKt, dt);
      if (ac.iasKt >= ac.perf.vrKt) {
        ac.onGround = false;
        setPhase(ac, 'departure');
        // Runway heading unless a heading was assigned before take-off (flown from 400 ft AGL).
        if (ac.nav.mode === 'heading' && ac.assigned.headingDeg === undefined) ac.nav.headingDeg = ac.departureRunway?.headingDeg ?? ac.headingDeg;
        ctx.emit({ type: 'airborne', ac });
      }
      return;
    }
    case 'abortRoll': {
      // A rejected take-off never rolls again from where it stopped: the crew vacates, taxis back
      // to the holding point and calls ready again. The runway stays occupied until it is clear.
      const rw = ac.departureRunway!;
      if (!ac.abortStopped) {
        ac.iasKt = Math.max(0, ac.iasKt - ac.perf.rolloutDecelKtS * dt);
        moveAlong(ac, ac.headingDeg, ac.iasKt, dt);
        if (ac.iasKt > 0) return;
        ac.abortStopped = true;
        ac.exitRoute = runwayExit(airport, rw, alongTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon));
        ctx.emit({ type: 'abortedTakeoff', ac });
        return;
      }
      const next = ac.exitRoute?.[0];
      // Without a taxiway exit, turn off towards the holding side so the way back does not cross.
      turnToward(ac, next ? bearingDeg(ac.lat, ac.lon, next.lat, next.lon) : (rw.headingDeg + 270) % 360, 18, dt);
      ac.iasKt = ac.groundspeedKt = 10;
      moveAlong(ac, ac.headingDeg, 10, dt);
      if (next && distanceM(ac.lat, ac.lon, next.lat, next.lon) < 12) ac.exitRoute!.shift();
      if (ac.exitRoute?.length || inRunwayArea(airport, rw, ac)) return;
      ac.abortStopped = false; ac.exitRoute = undefined;
      ac.iasKt = ac.groundspeedKt = 0;
      ac.taxiRoute = ac.holdShortPos ? taxiBack(airport, ac, ac.holdShortPos) : []; ac.taxiRouteIdx = 0;
      setPhase(ac, 'taxiOut');
      ctx.emit({ type: 'clearOfRunway', ac, runway: rw.name });
      return;
    }
    case 'rollout': {
      ac.iasKt = Math.max(0, ac.iasKt - ac.perf.rolloutDecelKtS * dt);
      moveAlong(ac, ac.headingDeg, ac.iasKt, dt);
      if (ac.iasKt <= ac.perf.exitKt) {
        if (ac.control && airport.groundNetwork) {
          const rw = ac.approach!.runway;
          ac.exitRoute ??= runwayExit(airport, rw, alongTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon));
          if (!ac.exitRoute) { ac.iasKt = ac.groundspeedKt = 0; return; }
          ac.iasKt = Math.max(8, ac.iasKt);
          if (distanceM(ac.lat, ac.lon, ac.exitRoute[0]!.lat, ac.exitRoute[0]!.lon) > 25) return;
        }
        setPhase(ac, 'vacating');
      }
      return;
    }
    case 'vacating': {
      const rw = ac.approach!.runway;
      if (ac.exitRoute?.length) {
        const next = ac.exitRoute[0]!;
        turnToward(ac, bearingDeg(ac.lat, ac.lon, next.lat, next.lon), 18, dt);
        ac.iasKt = ac.groundspeedKt = 10;
        moveAlong(ac, ac.headingDeg, 10, dt);
        if (distanceM(ac.lat, ac.lon, next.lat, next.lon) < 12) ac.exitRoute.shift();
        if (!ac.exitRoute.length && !inRunwayArea(airport, rw, ac)) {
          setPhase(ac, 'landed'); ac.removeAtS = ctx.simTimeS + 12;
          ctx.emit({ type: 'clearOfRunway', ac, runway: rw.name });
        }
        return;
      }
      turnToward(ac, (rw.headingDeg + 90) % 360, 12, dt);
      ac.iasKt = Math.min(ac.perf.exitKt, 12);
      moveAlong(ac, ac.headingDeg, ac.iasKt, dt);
      if (Math.abs(crossTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon)) >= 65) {
        setPhase(ac, 'landed');
        ac.removeAtS = ctx.simTimeS + 12;
        ctx.emit({ type: 'clearOfRunway', ac, runway: rw.name });
      }
      return;
    }
    case 'landed': case 'handedOff': case 'exited': case 'cancelled':
      if (ac.control && ac.phase === 'landed') { ac.iasKt = ac.groundspeedKt = 0; return; }
      ac.iasKt = Math.max(0, ac.iasKt - ac.perf.rolloutDecelKtS * dt);
      if (ac.phase === 'landed') moveAlong(ac, ac.headingDeg, ac.iasKt, dt);
      return;
  }

  // --- Airborne: guidance ---
  const tas = tasFromIas(ac.iasKt, ac.altitudeFt);
  let desiredHeading = ac.nav.headingDeg;
  let targetAlt = ac.targetAltitudeFt;
  let targetIas = defaultIas(ac);
  let allowClimb = true;

  switch (ac.nav.mode) {
    case 'heading': break;
    case 'direct': case 'route': {
      const leg = ac.nav.mode === 'direct' ? ({ kind: 'fix', fix: ac.nav.fix! } as RouteLeg) : ac.nav.route?.[ac.nav.routeIdx];
      if (!leg) { ac.nav.mode = 'heading'; break; }
      if (leg.kind === 'heading') {
        const next = ac.nav.mode === 'route' ? ac.nav.route?.[ac.nav.routeIdx + 1] : undefined;
        if (next?.kind !== 'fix') {
          // The procedure ends on a heading: radar vectors follow.
          ac.nav = { ...ac.nav, mode: 'heading', headingDeg: leg.headingDeg, turn: undefined }; desiredHeading = leg.headingDeg; break;
        }
        // A heading between fixes ("climb heading 264 to 7 000, then to NOGAH"): fly it until the
        // next fix's lowest altitude is reached, or, with none, until the turn onto it is done.
        desiredHeading = ac.nav.headingDeg = leg.headingDeg;
        const floor = next.restriction?.altitudeMinFt ?? next.restriction?.altitudeFt;
        if (floor !== undefined ? ac.altitudeFt >= floor - 50 : Math.abs(wrap180(ac.headingDeg - leg.headingDeg)) < 5) ac.nav.routeIdx++;
        break;
      }
      const fix = airport.fixes.get(leg.fix);
      if (!fix) { ac.nav.mode = 'heading'; break; }
      const brg = bearingDeg(ac.lat, ac.lon, fix.lat, fix.lon);
      if (passingFix(ac, fix, tas, opts)) {
        if (ac.nav.mode === 'direct') {
          // Direct to a fix on the flight plan is a shortcut: carry on along the plan from there.
          const idx = ac.flightPlan.findIndex((l) => l.kind === 'fix' && l.fix === leg.fix);
          if (idx >= 0 && idx + 1 < ac.flightPlan.length) {
            ac.nav = { ...ac.nav, mode: 'route', route: ac.flightPlan, routeIdx: idx + 1, fix: undefined, turn: undefined };
            ac.assigned.fix = undefined; ac.assigned.headingDeg = undefined;
            if (opts.descendVia && ac.assigned.altitudeFt === undefined) applyDescendVia(ac);
          } else {
            // The clearance limit: an arrival holds there when the fix has a published hold (ICAO
            // lost-clearance practice), otherwise it keeps its heading and asks.
            const hold = ac.kind === 'arrival' ? publishedHoldState(airport, leg.fix, 'outbound') : undefined;
            if (hold) { ac.nav = { ...ac.nav, mode: 'hold', headingDeg: ac.headingDeg, fix: undefined, turn: hold.turn, hold }; ac.assigned.fix = undefined; ac.assigned.hold = leg.fix; }
            else { ac.nav = { ...ac.nav, mode: 'heading', headingDeg: ac.headingDeg, fix: undefined, turn: undefined }; ac.assigned.fix = undefined; ac.assigned.headingDeg = Math.round(toMagneticDeg(airport, ac.headingDeg)) || 360; }
          }
          ctx.emit({ type: 'reachedFix', ac, fix: leg.fix, next: ac.nav.mode === 'route' ? 'route' : ac.nav.mode === 'hold' ? 'hold' : idx >= 0 ? 'endOfRoute' : 'offRoute' });
        } else {
          ac.nav.routeIdx++;
          if (opts.descendVia && ac.assigned.altitudeFt === undefined) applyDescendVia(ac);
          const ended = ac.nav.routeIdx >= (ac.nav.route?.length ?? 0);
          if (ended) { ac.nav = { ...ac.nav, mode: 'heading', headingDeg: ac.headingDeg, turn: undefined }; }
          ctx.emit({ type: 'reachedFix', ac, fix: leg.fix, next: ended ? 'endOfRoute' : 'route' });
        }
        desiredHeading = ac.nav.headingDeg;
      } else {
        desiredHeading = brg;
        ac.nav.headingDeg = brg;
      }
      break;
    }
    case 'hold': {
      const h = ac.nav.hold!;
      const fix = airport.fixes.get(h.fix)!;
      const d = distanceM(ac.lat, ac.lon, fix.lat, fix.lon);
      h.t += dt;
      // Change stage first, so the forced turn starts toward the new heading on this same tick
      // (turning toward the old one, already reached, would release it).
      if (h.stage !== 'outbound' && d < 0.4 * NM_TO_M) { h.stage = 'outbound'; h.t = 0; if (!h.published) h.inboundDeg = ac.headingDeg; ac.nav.turn = h.turn ?? 'R'; }
      else if (h.stage === 'outbound' && h.t > h.legS) { h.stage = 'inbound'; h.t = 0; ac.nav.turn = h.turn ?? 'R'; }
      desiredHeading = h.stage === 'outbound' ? (h.inboundDeg + 180) % 360 : bearingDeg(ac.lat, ac.lon, fix.lat, fix.lon);
      if (h.maxIasKt !== undefined) targetIas = Math.min(targetIas, h.maxIasKt);
      ac.nav.headingDeg = desiredHeading;
      break;
    }
    case 'localizer': {
      const ap = ac.approach!;
      const rw = ap.runway;
      const xtk = crossTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon);
      const along = alongTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon);
      const distToThrM = -along;
      if (!ap.established) {
        const via = ap.via?.[0];
        const viaFix = via && airport.fixes.get(via.fix);
        if (via && viaFix) {
          // Flying the procedure to the localizer: fix to fix, down to each segment's published
          // altitude but never above what the controller already allowed, and never climbing.
          const lowest = via.altitudeFt !== undefined ? Math.max(via.altitudeFt, fieldFt + 1000) : undefined;
          const from = ap.viaFrom ? airport.fixes.get(ap.viaFrom) : undefined;
          const after = ap.via![1] ? airport.fixes.get(ap.via![1].fix) : undefined;
          const nextCourse = after ? bearingDeg(viaFix.lat, viaFix.lon, after.lat, after.lon) : rw.headingDeg;
          if (passingFix(ac, viaFix, tas, opts, nextCourse, from ? bearingDeg(from.lat, from.lon, viaFix.lat, viaFix.lon) : ac.headingDeg)) {
            // Past the fix, its altitude is the one to keep until the next segment allows lower.
            if (lowest !== undefined && lowest < ac.targetAltitudeFt) ac.targetAltitudeFt = lowest;
            ap.via!.shift();
            ap.viaFrom = via.fix;
            ctx.emit({ type: 'reachedFix', ac, fix: via.fix, next: 'route' });
          } else if (from) {
            // Track the leg's course, correcting back onto it at up to 30° like the localizer tracking below.
            const course = bearingDeg(from.lat, from.lon, viaFix.lat, viaFix.lon);
            const off = crossTrackM(from.lat, from.lon, course, ac.lat, ac.lon) / NM_TO_M;
            ac.nav.headingDeg = (course - Math.max(-30, Math.min(30, off * 25)) + 360) % 360;
          } else ac.nav.headingDeg = bearingDeg(ac.lat, ac.lon, viaFix.lat, viaFix.lon);
          if (lowest !== undefined) targetAlt = Math.min(ac.targetAltitudeFt, lowest);
          allowClimb = false;
        } else if (via) ap.via!.shift();
        desiredHeading = ac.nav.headingDeg;
        // Hold the current speed (or the assigned one) until established; no accelerating on the intercept.
        targetIas = ac.targetIasKt ?? Math.min(ac.iasKt, 210);
        const theta = wrap180(ac.headingDeg - rw.headingDeg);
        const closing = xtk * Math.sin((theta * Math.PI) / 180) < 0;
        const omega = (turnRateDegS(tas, ac.perf.bankDeg, opts.bankLimitedTurns) * Math.PI) / 180;
        const R = (tas * KT_TO_MPS) / omega;
        const lead = R * (1 - Math.cos((Math.abs(theta) * Math.PI) / 180));
        // The intercept heading the crew was given, not the heading mid-turn, decides whether it captures.
        // On the way to the procedure it only joins along a leg of it, not across the final short of the fixes.
        const steep = Math.abs(wrap180(ac.nav.headingDeg - rw.headingDeg)) > (ap.via?.length ? Math.min(45, opts.maxInterceptDeg) : opts.maxInterceptDeg);
        const overshot = ap.clearedSide !== undefined && ap.clearedSide !== 0 && Math.sign(xtk) === -ap.clearedSide && Math.abs(xtk) > 0.5 * NM_TO_M;
        // Already inside the beam and nearly parallel to it: about a degree either side of the
        // centreline, as a localizer is, so a published procedure whose fixes sit a few hundred
        // metres off our runway line still captures along its legs.
        const inBeam = Math.abs(xtk) < Math.max(60, distToThrM * BEAM_CAPTURE_TAN) && Math.abs(theta) < 10;
        if (!steep && distToThrM > 0 && Math.abs(theta) <= 90 && ((closing && Math.abs(xtk) <= lead + 40) || inBeam)) {
          ap.established = true;
          ac.nav.turn = undefined;
          setPhase(ac, 'final');
          ctx.emit({ type: 'established', ac });
        } else if (!ap.missed && (along > 1.5 * NM_TO_M || (!ap.via?.length && ((steep && overshot) || (Math.abs(theta) > 90 && distToThrM / NM_TO_M > (ap.clearedAtNm ?? Infinity) + DIVERGED_NM))))) {
          // Past the threshold without capturing, through the final on too steep a cut, or pointing
          // away from the runway and steadily further from it than when the clearance was given:
          // this approach is not happening. Flying out to the procedure's first fix is neither.
          ap.missed = true;
          ctx.emit({ type: 'missedIntercept', ac });
        }
      }
      if (ap.established) {
        const correction = Math.max(-30, Math.min(30, (xtk / NM_TO_M) * 25));
        desiredHeading = (rw.headingDeg - correction + 360) % 360;
        ac.nav.headingDeg = rw.headingDeg;
        // Glideslope to a touchdown aim point 300 m past the threshold, 50 ft threshold crossing.
        const slopeAltFt = rw.elevationM / FT_TO_M + Math.tan((rw.glideslopeDeg * Math.PI) / 180) * Math.max(0, distToThrM + 300) / FT_TO_M;
        // Captured from below, or from above by descending back onto it: the descent rate on the
        // slope is capped (see below), so a clearance given far too high runs out of height and
        // ends in the go-around at 3 nm rather than a dive.
        if (!ap.onSlope && ac.altitudeFt >= slopeAltFt - 30) ap.onSlope = true;
        if (ap.onSlope) targetAlt = slopeAltFt;
        else { targetAlt = Math.min(ac.targetAltitudeFt, ac.altitudeFt); }
        allowClimb = false;
        const distNm = distToThrM / NM_TO_M;
        targetIas = distNm < 8 ? ac.perf.approachKt : Math.max(ac.perf.approachKt, Math.min(ac.targetIasKt ?? 180, 180));
        // Too high to land from here at every realism level (an approach clearance carries no
        // altitude limit, so this is the backstop); above mid, the softer stabilisation criteria too.
        const unlandable = ac.altitudeFt > slopeAltFt + UNLANDABLE_FT;
        const unstable = opts.unstableApproachGoAround && (ac.altitudeFt > slopeAltFt + 800 || ac.iasKt > ac.perf.approachKt + 40);
        if (distNm < 3 && distNm > 2.5 && (unlandable || unstable)) { goAround(ac, ctx, 'unstable approach'); return; }
        if (!ap.cleared) {
          if (distNm < 3 && !ap.requestedLanding) { ap.requestedLanding = true; ctx.emit({ type: 'requestLanding', ac }); }
          if (distNm < 1) { goAround(ac, ctx, 'no landing clearance'); return; }
        }
        // A crew can reject an occupied runway on final, but cannot instantly climb away
        // after passing the decision point in the flare.
        if (distNm < 1.2 && distNm > 0.3 && ctx.occupiedRunways.has(runwayPairName(airport, rw))) {
          goAround(ac, ctx, 'runway occupied'); return;
        }
        // Touchdown.
        const aglFt = ac.altitudeFt - rw.elevationM / FT_TO_M;
        if (along >= 0 && aglFt <= 30) {
          ac.onGround = true; ac.altitudeFt = rw.elevationM / FT_TO_M; ac.verticalSpeedFpm = 0;
          ac.headingDeg = rw.headingDeg; ac.trackDeg = rw.headingDeg;
          setPhase(ac, 'rollout');
          ctx.emit({ type: 'touchdown', ac, runway: rw.name });
          return;
        }
      }
      break;
    }
  }

  // Departures fly runway heading until 400 ft AGL, then whatever is assigned.
  if (ac.phase === 'departure' && ac.altitudeFt < fieldFt + 400) desiredHeading = ac.departureRunway?.headingDeg ?? ac.headingDeg;
  if (ac.phase === 'departure' && ac.altitudeFt >= fieldFt + 1000) setPhase(ac, 'enroute');
  if (ac.phase === 'goAround' && ac.altitudeFt >= fieldFt + 600) setPhase(ac, 'enroute');

  // --- Heading ---
  const rate = turnRateDegS(tas, ac.perf.bankDeg, opts.bankLimitedTurns);
  turnToward(ac, desiredHeading, rate, dt);

  // --- Altitude ---
  if (ac.targetIasKt !== null && ac.nav.mode !== 'localizer') targetIas = clampIas(ac, ac.targetIasKt);
  const diff = targetAlt - ac.altitudeFt;
  if (diff > 0 && !allowClimb) { ac.verticalSpeedFpm = 0; }
  else {
    let rateFpm = diff > 0 ? climbRateFpm(ac.perf, ac.altitudeFt, opts.altitudeDependentClimb) : ac.perf.descentFpm;
    if (ac.expedite) rateFpm *= EXPEDITE_FACTOR;
    if (ac.nav.mode === 'localizer' && ac.approach?.onSlope) rateFpm = Math.min(rateFpm, 1500);
    const maxStep = (rateFpm / 60) * dt;
    if (Math.abs(diff) <= maxStep) { ac.verticalSpeedFpm = (diff / dt) * 60 * 0.5; ac.altitudeFt = targetAlt; if (ac.nav.mode !== 'localizer') ac.expedite = false; }
    else { ac.altitudeFt += Math.sign(diff) * maxStep; ac.verticalSpeedFpm = Math.sign(diff) * rateFpm; }
  }
  // Never below ground in the air; on approach the ground is the runway threshold, which can sit below the field elevation.
  const groundFt = ac.approach ? ac.approach.runway.elevationM / FT_TO_M : fieldFt;
  if (ac.altitudeFt <= groundFt && !ac.onGround) ac.altitudeFt = groundFt + 1;

  // --- Speed ---
  const dIas = targetIas - ac.iasKt;
  ac.iasKt += Math.max(-ac.perf.decelKtS * dt, Math.min(ac.perf.accelKtS * dt, dIas));

  // --- Wind and motion ---
  const tasNow = tasFromIas(ac.iasKt, ac.altitudeFt);
  let gs = tasNow, track = ac.headingDeg;
  if (opts.wind !== 'none' && ctx.wind.speedKt > 0) {
    const rel = ((ctx.wind.directionDeg - ac.headingDeg) * Math.PI) / 180;
    const head = ctx.wind.speedKt * Math.cos(rel);
    const cross = ctx.wind.speedKt * Math.sin(rel); // positive = wind from the right
    gs = Math.max(30, tasNow - head);
    track = (ac.headingDeg - (Math.asin(Math.max(-1, Math.min(1, cross / tasNow))) * 180) / Math.PI + 360) % 360;
  }
  ac.groundspeedKt = gs;
  ac.trackDeg = track;
  const p = destination(ac.lat, ac.lon, track, gs * KT_TO_MPS * dt);
  ac.lat = p.lat; ac.lon = p.lon;

  // --- Handoff / exit ---
  if (!ac.control && ac.kind === 'departure' && ac.phase === 'enroute' && ac.requested && !ac.returning) {
    const fix = airport.fixes.get(ac.requested);
    if (fix && distanceM(ac.lat, ac.lon, fix.lat, fix.lon) < 1 * NM_TO_M && ac.altitudeFt >= fieldFt + 4000) {
      setPhase(ac, 'handedOff');
      ac.removeAtS = ctx.simTimeS + 5;
      ctx.emit({ type: 'handoff', ac, fix: ac.requested });
    }
  }
}

function defaultIas(ac: Aircraft): number {
  const p = ac.perf;
  if (ac.phase === 'departure') return Math.min(p.maxLowKt, p.cruiseLowKt);
  return ac.altitudeFt <= 10000 ? p.cruiseLowKt : p.cruiseKt;
}

export function clampIas(ac: Aircraft, kt: number): number {
  const max = ac.altitudeFt < 10000 ? ac.perf.maxLowKt : ac.perf.maxKt;
  return Math.max(ac.perf.minKt, Math.min(max, kt));
}

function applyDescendVia(ac: Aircraft): void {
  const next = ac.nav.route?.slice(ac.nav.routeIdx).find((l) => l.kind === 'fix' && l.restriction);
  if (!next || next.kind !== 'fix' || !next.restriction) return;
  const r = next.restriction;
  const alt = r.altitudeFt ?? r.altitudeMaxFt;
  if (alt !== undefined && alt < ac.targetAltitudeFt) ac.targetAltitudeFt = alt;
  const spd = r.speedKt ?? r.speedMaxKt;
  if (spd !== undefined && ac.targetIasKt === null) ac.targetIasKt = spd;
}

function turnToward(ac: Aircraft, desired: number, rateDegS: number, dt: number): void {
  let delta = wrap180(desired - ac.headingDeg);
  if (ac.nav.turn === 'L' && delta > 0.5) delta -= 360;
  if (ac.nav.turn === 'R' && delta < -0.5) delta += 360;
  const step = rateDegS * dt;
  if (Math.abs(delta) <= step) { ac.headingDeg = (desired + 360) % 360; ac.nav.turn = undefined; }
  else ac.headingDeg = (ac.headingDeg + Math.sign(delta) * step + 360) % 360;
}

function moveAlong(ac: Aircraft, headingDeg: number, kt: number, dt: number): void {
  ac.groundspeedKt = kt; ac.trackDeg = headingDeg; ac.verticalSpeedFpm = 0;
  if (kt <= 0) return;
  const p = destination(ac.lat, ac.lon, headingDeg, kt * KT_TO_MPS * dt);
  ac.lat = p.lat; ac.lon = p.lon;
}
